/**
 * The allowed types for a database key.
 * It can be a string, number, Date, or an array of string, number, or Date.
 */
export type DbKeyType = string | number | Date | DbKeyRange;

export interface DbKeyRange {
  lower?: any;
  upper?: any;
  lowerOpen?: boolean;
  upperOpen?: boolean;
}

/**
 * Generic database store.
 */
export interface DbStore {
  /**
   * Adds a record to the store.
   * @param record The record to be added.
  */
  create(record: any): Promise<any>;

  /**
   * Updates record in the store.
   * @param record The record to be updated.
  */
  update(record: any): Promise<any>;

  /**
   * Gets a record by primary key.
   * @param primaryKey The primary key of the record.
   */
  get(primaryKey: number): Promise<any>;

  /**
   * Gets all records from the store.
  */
  getAll(): Promise<any>;

   /**
   * Gets partial data for all records in the store.
   * @param partialFields The fields to include in the partial data.
   * @returns A promise that resolves with an array of partial data objects.
   */
   getAllPartialFields<T>(T: new (record: any) => T): Promise<any>;
   /**
    * Gets partial data for all records in the store by index.
    * @param indexName The name of the index.
    * @param indexKey The index key of the records.
    * @param partialFields The fields to include in the partial data.
    * @returns A promise that resolves with an array of partial data objects
    */
   getAllPartialFieldsByIndex<T>(indexName: string, indexKey: DbKeyType, T: new (record: any) => T): Promise<any>;

  /**
   * Gets a record by index key. If there are multiple records with the same index key, the first one is returned.
   * @param indexName The name of the index.
   * @param indexKey The index key of the record.
   */
  getByIndex(indexName: string, indexKey: DbKeyType): Promise<any>;

     /**
   * Gets partial data for a record by index key. If there are multiple records with the same index key, the first one is returned.
   * @param indexName The name of the index.
   * @param indexKey The index key of the record.
   * @param partialFields The fields to include in the partial data.
   */
    getPartialFieldsByIndex<T>(indexName: string, indexKey: DbKeyType, T: new (record: any) => T): Promise<any>;

  /**
   * Gets all records matching the index key.
   * @param indexName The name of the index.
   * @param indexKey The index key of the records.
   */
  getAllByIndex(indexName: string, indexKey: DbKeyType): Promise<any>;

  /**
   * Deletes a record from the store.
   * @param record The record to be deleted.
   */
  delete(record: any): Promise<any>;

  /**
  * Opens a cursor on the store.
  */
  openCursor(): IDBRequest<IDBCursorWithValue>;
}

/**
 * IndexedDb implementation of DbStore interface.
 */
export class IndexedDbStore implements DbStore {
  constructor(private store: IDBObjectStore) { }

  private get primaryKeyPaths(): string[] {
    return Array.isArray(this.store.keyPath) ? this.store.keyPath : [this.store.keyPath];
  }

  private isDbKeyRange(key: any): key is DbKeyRange {
    return key && typeof key === 'object' && 'lower' in key && 'upper' in key;
  }

  private createKeyRange(range: DbKeyRange): IDBKeyRange {
    const lowerOpen = range.lowerOpen || false;
    const upperOpen = range.upperOpen || false;
    return IDBKeyRange.bound(range.lower, range.upper, lowerOpen, upperOpen);
  }

  create(record: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const recordCopy = { ...record };
      this.primaryKeyPaths.forEach(key => {
        // Need to delete any not-truthy keys to prevent IndexedDB error
        if (key in recordCopy && !recordCopy[key]) {
          delete recordCopy[key];
        }
      });
      const request = this.store.put(recordCopy);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  update(record: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = this.store.put(record);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  get(primaryKey: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = this.store.get(primaryKey);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  getAll(): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = this.store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  getByIndex(indexName: string, indexKey: DbKeyType): Promise<any> {
    return new Promise((resolve, reject) => {
      const index = this.store.index(indexName);
      let idbKey;

      if (this.isDbKeyRange(indexKey)) {
        idbKey = this.createKeyRange(indexKey);
      }
      else {
        idbKey = indexKey;
      }

      const request = index.get(idbKey);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  getPartialFieldsByIndex<T>(indexName: string, indexKey: DbKeyType, T: new (record: any) => T): Promise<any> {
    return new Promise((resolve, reject) => {
      const index = this.store.index(indexName);
      let idbKey;

      if (this.isDbKeyRange(indexKey)) {
        idbKey = this.createKeyRange(indexKey);
      }
      else {
        idbKey = indexKey;
      }

      const request = index.get(idbKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result == null) {
          resolve(null);
        } else {
          resolve(new T(result));
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  getAllByIndex(indexName: string, indexKey: DbKeyType): Promise<any> {
    return new Promise((resolve, reject) => {
      const index = this.store.index(indexName);
      let idbKey;

      if (this.isDbKeyRange(indexKey)) {
        idbKey = this.createKeyRange(indexKey);
      }
      else {
        idbKey = indexKey;
      }

      const request = index.getAll(idbKey);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  getAllPartialFields<T>(T: new (record: any) => T): Promise<any> {
    const partialData = [];
    return new Promise<any[]>((resolve, reject) => {
      const cursorRequest = this.store.openCursor();
      cursorRequest.onsuccess = (event: Event) => {
        const cursor: IDBCursorWithValue = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const record = cursor.value;
          const partialDataRecord = new T(record);
          partialData.push(partialDataRecord);
          cursor.continue();
        } else {
          resolve(partialData);
        }
      };
      cursorRequest.onerror = (event: Event) => {
        reject((event.target as IDBRequest).error);
      };
    });
  }
  getAllPartialFieldsByIndex<T>(indexName: string, indexKey: DbKeyType,T: new (record: any) => T): Promise<any> {
    const partialData = [];
    return new Promise<any[]>((resolve, reject) => {
      const index = this.store.index(indexName);
      let idbKey;
      if (this.isDbKeyRange(indexKey)) {
        idbKey = this.createKeyRange(indexKey);
      } else {
        idbKey = indexKey;
      }
      const cursorRequest = index.openCursor(idbKey);
      cursorRequest.onsuccess = (event: Event) => {
        const cursor: IDBCursorWithValue = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const record = cursor.value;
          const recordPartialData = new T(record);
          partialData.push(recordPartialData);
          cursor.continue();
        } else {
          resolve(partialData);
        }
      };
      cursorRequest.onerror = (event: Event) => {
        reject((event.target as IDBRequest).error);
      };
    });
  }


  delete(primaryKey: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = this.store.delete(primaryKey);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  openCursor(): IDBRequest<IDBCursorWithValue> {
    const request = this.store.openCursor();
    return request;
  }

}
